import { sanitizeTickerFilterValue } from "../shared/tickerFilterInput";
import { normalizeTicker } from "./watchlistData";

export type WheelTickerFilterTarget =
  | "options"
  | "puts"
  | "puts-plus"
  | "calls"
  | "calls-plus"
  | "stocks"
  | "history";

export const WHEEL_TICKER_FILTER_EVENT = "sfe-wheel-ticker-filter";

const STORAGE_KEYS: Record<WheelTickerFilterTarget, string> = {
  options: "sfe-web-options-filters-v1",
  puts: "sfe-web-put-filters-v1",
  "puts-plus": "sfe-web-tradier-put-filters-v1",
  calls: "sfe-web-call-filters-v1",
  "calls-plus": "sfe-web-tradier-call-filters-v1",
  stocks: "sfe-web-stocks-filters-v1",
  history: "sfe-web-history-filters-v1",
};

const INPUT_IDS: Record<WheelTickerFilterTarget, string> = {
  options: "wheelOptTicker",
  puts: "wheelPutTicker",
  "puts-plus": "tradierPutTicker",
  calls: "wheelCallTicker",
  "calls-plus": "tradierCallTicker",
  stocks: "wheelStockTicker",
  history: "wheelHistTicker",
};

function patchStoredTicker(key: string, ticker: string): void {
  try {
    const raw = localStorage.getItem(key);
    const base = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    base.ticker = ticker;
    localStorage.setItem(key, JSON.stringify(base));
  } catch {
    localStorage.setItem(key, JSON.stringify({ ticker }));
  }
}

export function readWheelTabTickerFilter(target: WheelTickerFilterTarget): string {
  const input = filterInputForTarget(target);
  if (input && inputIsOnVisiblePanel(input)) {
    return sanitizeTickerFilterValue(input.value).toUpperCase();
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[target]);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { ticker?: string };
    return sanitizeTickerFilterValue(String(parsed.ticker ?? "")).toUpperCase();
  } catch {
    return "";
  }
}

export function clearWheelTabTickerFilter(target: WheelTickerFilterTarget): boolean {
  patchStoredTicker(STORAGE_KEYS[target], "");

  const input = filterInputForTarget(target);
  if (input && inputIsOnVisiblePanel(input)) {
    input.value = "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    document.dispatchEvent(
      new CustomEvent(WHEEL_TICKER_FILTER_EVENT, {
        detail: { target, ticker: "" },
      }),
    );
    return true;
  }

  document.dispatchEvent(
    new CustomEvent(WHEEL_TICKER_FILTER_EVENT, {
      detail: { target, ticker: "" },
    }),
  );
  return true;
}

export function isWheelTabTickerFilterActive(
  target: WheelTickerFilterTarget,
  ticker: string,
): boolean {
  const normalized = sanitizeTickerFilterValue(normalizeTicker(ticker)).toUpperCase();
  if (!normalized) return false;
  return readWheelTabTickerFilter(target) === normalized;
}

export function wheelTargetForRecSide(side: string | null | undefined): "puts" | "calls" {
  return String(side || "").toLowerCase().startsWith("c") ? "calls" : "puts";
}

/** Resolve Puts+/Calls+ when Focus is invoked from a Tradier board row. */
export function wheelTargetForRecHost(
  host: HTMLElement,
  side: string | null | undefined,
): WheelTickerFilterTarget {
  const stamped = host.getAttribute("data-wheel-filter-target")?.trim();
  if (
    stamped === "puts-plus" ||
    stamped === "calls-plus" ||
    stamped === "puts" ||
    stamped === "calls" ||
    stamped === "options" ||
    stamped === "stocks" ||
    stamped === "history"
  ) {
    return stamped;
  }
  // Tradier Puts+/Calls+ boards — never fall through to Puts/Calls.
  const tradier = host.closest<HTMLElement>(".sfe-tradier-wheel");
  if (tradier) {
    const tradierView = tradier.getAttribute("data-wheel-view")?.trim();
    if (tradierView === "puts-plus" || tradierView === "calls-plus") return tradierView;
    return String(side || "").toLowerCase().startsWith("c") ? "calls-plus" : "puts-plus";
  }
  const view = host.closest<HTMLElement>("[data-wheel-view]")?.getAttribute("data-wheel-view")?.trim();
  if (view === "puts-plus" || view === "calls-plus") return view;
  const tableId = host.closest("table")?.id ?? "";
  if (tableId === "tradierPutsTable") return "puts-plus";
  if (tableId === "tradierCallsTable") return "calls-plus";
  return wheelTargetForRecSide(side);
}

function filterInputForTarget(target: WheelTickerFilterTarget): HTMLInputElement | null {
  const id = INPUT_IDS[target];
  const scoped = document.querySelector<HTMLInputElement>(
    `[data-wheel-view="${CSS.escape(target)}"] #${CSS.escape(id)}`,
  );
  if (scoped) return scoped;
  const el = document.getElementById(id);
  return el instanceof HTMLInputElement ? el : null;
}

function inputIsOnVisiblePanel(input: HTMLInputElement): boolean {
  const panel = input.closest<HTMLElement>(".sfe-web-panel, [data-panel], [data-tradier-wheel-host]");
  if (!panel) return input.isConnected;
  if (panel.hidden) return false;
  if (panel.getAttribute("hidden") != null && panel.getAttribute("hidden") !== "false") return false;
  return true;
}

export function screenerKindForFilterTarget(
  target: string | null | undefined,
): "puts" | "calls" | undefined {
  if (target === "puts" || target === "puts-plus") return "puts";
  if (target === "calls" || target === "calls-plus") return "calls";
  return undefined;
}

/** Prefix-match ticker filter for a wheel tab; updates storage and live filter input when present. */
export function applyWheelTabTickerFilter(target: WheelTickerFilterTarget, ticker: string): boolean {
  const normalized = sanitizeTickerFilterValue(normalizeTicker(ticker)).toUpperCase();
  if (!normalized) return false;

  patchStoredTicker(STORAGE_KEYS[target], normalized);

  // Always notify listeners (Puts+/Calls+ paint from this). Do not touch hidden Puts/Calls inputs.
  document.dispatchEvent(
    new CustomEvent(WHEEL_TICKER_FILTER_EVENT, {
      detail: { target, ticker: normalized },
    }),
  );

  const input = filterInputForTarget(target);
  if (input && inputIsOnVisiblePanel(input)) {
    input.value = normalized;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus({ preventScroll: true });
    input.closest(".filter-bar")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return true;
  }

  return true;
}

/** Apply filter for ticker, or clear when that ticker is already the active filter. Returns true if filter is on after. */
export function toggleWheelTabTickerFilter(
  target: WheelTickerFilterTarget,
  ticker: string,
): boolean {
  if (isWheelTabTickerFilterActive(target, ticker)) {
    clearWheelTabTickerFilter(target);
    return false;
  }
  applyWheelTabTickerFilter(target, ticker);
  return true;
}
